import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import GstDeliveryBox from './GstDeliveryBox';
import { msg } from './gstUi';

const EMPTY = { legalName: '', tradeName: '', gstin: '', pan: '', address: '', phone: '', email: '', invoicePrefix: '', numberFormat: 'plain', fyStartMonth: 4, logo: '',
  bank: { name: '', accountNo: '', ifsc: '', branch: '' }, terms: [] };

export default function GstCompanyTab() {
  const { call } = useGst();
  const [f, setF] = useState(EMPTY);
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextNo, setNextNo] = useState('');

  useEffect(() => {
    call(() => gstAPI.company()).then((b) => {
      if (b.data) { setF({ ...EMPTY, ...b.data, bank: { ...EMPTY.bank, ...(b.data.bank || {}) } }); setTerms((b.data.terms || []).join('\n')); }
    }).catch((e) => toast.error(msg(e))).finally(() => setLoading(false));
  }, [call]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setBank = (k) => (e) => setF((p) => ({ ...p, bank: { ...p.bank, [k]: e.target.value } }));

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type) || file.size > 250 * 1024) { toast.error('Logo must be a PNG or JPEG under 250 KB'); return; }
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, logo: r.result }));
    r.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const b = await call(() => gstAPI.saveCompany({ ...f, numberFormat: 'plain', invoicePrefix: '', terms: terms.split('\n').map((t) => t.trim()).filter(Boolean) }));
      setF((p) => ({ ...p, ...b.data })); toast.success('Company saved');
    } catch (err) { toast.error(msg(err)); } finally { setSaving(false); }
  };

  const startAt = async () => {
    if (!window.confirm(`Make the next invoice number ${nextNo}? Numbers can only move forward, and skipped numbers will show as a gap.`)) return;
    try {
      const b = await call(() => gstAPI.setNextNumber(Number(nextNo)));
      toast.success(`Next invoice will be ${b.data.next} (FY ${b.data.fy})`);
      setNextNo('');
    } catch (e) { toast.error(msg(e)); }
  };

  if (loading) return <div className="spinner" />;
  const field = (label, node) => <div className="form-group" style={{ flex: '1 1 240px' }}><label className="form-label">{label}</label>{node}</div>;
  const inp = (k, extra = {}) => <input className="form-control" value={f[k] || ''} onChange={set(k)} {...extra} />;
  return (
    <>
    <form className="card" style={{ padding: 16 }} onSubmit={save}>
      <p style={{ opacity: 0.7, marginTop: 0 }}>The state is taken from the GSTIN. The financial-year start cannot change once an invoice exists.</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('Legal name *', inp('legalName'))}
        {field('Trade name', inp('tradeName'))}
        {field('GSTIN *', inp('gstin', { maxLength: 15, style: { textTransform: 'uppercase' } }))}
        {field('PAN', inp('pan', { maxLength: 10, style: { textTransform: 'uppercase' } }))}
        {field('Financial year starts in month', <input className="form-control" type="number" min="1" max="12" value={f.fyStartMonth} onChange={set('fyStartMonth')} />)}
        {field('Phone', inp('phone'))}
        {field('Email', inp('email'))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('Invoice numbering', <div style={{ paddingTop: 8 }}>Plain numbers (… 18, 19, 20 …)</div>)}
        {field('Start next invoice at number', (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="form-control" type="number" min="1" value={nextNo} onChange={(e) => setNextNo(e.target.value)} placeholder="e.g. 18" />
            <button type="button" className="btn btn-secondary" disabled={!nextNo} onClick={startAt}>Set</button>
          </div>
        ))}
      </div>
      <div className="form-group"><label className="form-label">Address *</label><textarea className="form-control" rows={2} value={f.address || ''} onChange={set('address')} /></div>
      <h4>Bank details</h4>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {field('Bank', <input className="form-control" value={f.bank.name} onChange={setBank('name')} />)}
        {field('Account no.', <input className="form-control" value={f.bank.accountNo} onChange={setBank('accountNo')} />)}
        {field('IFSC', <input className="form-control" value={f.bank.ifsc} onChange={setBank('ifsc')} style={{ textTransform: 'uppercase' }} />)}
        {field('Branch', <input className="form-control" value={f.bank.branch} onChange={setBank('branch')} />)}
      </div>
      <div className="form-group"><label className="form-label">Terms (one per line)</label><textarea className="form-control" rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
      <div className="form-group">
        <label className="form-label">Logo (PNG/JPEG, ≤ 250 KB)</label>
        <input type="file" accept="image/png,image/jpeg" onChange={pickLogo} />
        {f.logo && <div style={{ marginTop: 6 }}><img src={f.logo} alt="logo" style={{ maxHeight: 50 }} /> <button type="button" className="btn btn-secondary" onClick={() => setF((p) => ({ ...p, logo: '' }))}>Remove</button></div>}
      </div>
      <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save company'}</button>
    </form>
    <GstDeliveryBox />
    </>
  );
}
